import React, { useState, useEffect } from "react";
import {
  X,
  CreditCard,
  Banknote,
  Split,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Hash,
} from "lucide-react";
import toast from "react-hot-toast";
import { AppointmentService } from "../../../../services/api/appointment.service";
import { DoctorPayment } from "../types";
import { UpdatePaymentMethodPayload, SplitPaymentLeg } from "../../../../services/api/types";

interface UpdatePaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: string;
  doctorPayments?: DoctorPayment;
  dhrId?: string;
  reload: () => void;
}

export const UpdatePaymentMethodModal: React.FC<UpdatePaymentMethodModalProps> = ({
  isOpen,
  onClose,
  appointmentId,
  doctorPayments,
  dhrId,
  reload,
}) => {
  const [paymentType, setPaymentType] = useState<"offline" | "online" | "split">("offline");
  const [cashAmount, setCashAmount] = useState<number>(0);
  const [onlineAmount, setOnlineAmount] = useState<number>(0);
  const [paymentTxnId, setPaymentTxnId] = useState<string>("");
  const [razorpayOrderId, setRazorpayOrderId] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Compute total collected/recorded amount so far
  const totalFee = Number(doctorPayments?.amount) || 0;
  const balanceDue = Number(doctorPayments?.balance_due) || 0;
  const currentCash = Number(doctorPayments?.cash_amount) || 0;
  const currentOnline = Number(doctorPayments?.online_amount) || 0;
  const currentAdvance = Number(doctorPayments?.advance_amount) || 0;

  const collectedAmount =
    currentCash + currentOnline > 0
      ? currentCash + currentOnline
      : currentAdvance > 0
      ? currentAdvance
      : Math.max(0, totalFee - balanceDue);

  useEffect(() => {
    if (isOpen) {
      const isCurrentlySplit =
        doctorPayments?.is_split || (currentCash > 0 && currentOnline > 0);
      const isCurrentlyOnline =
        !isCurrentlySplit &&
        (currentOnline > 0 ||
          (doctorPayments?.status || "").toLowerCase() === "online");

      if (isCurrentlySplit) {
        setPaymentType("split");
        setCashAmount(currentCash > 0 ? currentCash : Math.round(collectedAmount / 2));
        setOnlineAmount(
          currentOnline > 0 ? currentOnline : collectedAmount - Math.round(collectedAmount / 2)
        );
      } else if (isCurrentlyOnline) {
        setPaymentType("online");
        setCashAmount(0);
        setOnlineAmount(collectedAmount);
      } else {
        setPaymentType("offline");
        setCashAmount(collectedAmount);
        setOnlineAmount(0);
      }

      setPaymentTxnId(doctorPayments?.payment_txn_id || "");
      setRazorpayOrderId("");
    }
  }, [isOpen, doctorPayments, collectedAmount, currentCash, currentOnline]);

  if (!isOpen) return null;

  const handleTypeChange = (type: "offline" | "online" | "split") => {
    setPaymentType(type);
    if (type === "offline") {
      setCashAmount(collectedAmount);
      setOnlineAmount(0);
    } else if (type === "online") {
      setCashAmount(0);
      setOnlineAmount(collectedAmount);
    } else if (type === "split") {
      const half = Math.round(collectedAmount / 2);
      setCashAmount(half);
      setOnlineAmount(collectedAmount - half);
    }
  };

  const handleCashChange = (val: number) => {
    const cash = Math.max(0, val);
    setCashAmount(cash);
    setOnlineAmount(Math.max(0, collectedAmount - cash));
  };

  const handleOnlineChange = (val: number) => {
    const online = Math.max(0, val);
    setOnlineAmount(online);
    setCashAmount(Math.max(0, collectedAmount - online));
  };

  const currentSplitTotal = (Number(cashAmount) || 0) + (Number(onlineAmount) || 0);
  const isSplitValid = paymentType !== "split" || currentSplitTotal === collectedAmount;

  const handleSubmit = async () => {
    if (collectedAmount <= 0) {
      toast.error("No recorded payment found to update");
      return;
    }

    const cash = Number(cashAmount) || 0;
    const online = Number(onlineAmount) || 0;

    if (cash < 0 || online < 0) {
      toast.error("Amounts cannot be negative");
      return;
    }

    if (paymentType === "split" && cash + online !== collectedAmount) {
      toast.error(`Split payments (₹${cash + online}) must total collected amount (₹${collectedAmount})`);
      return;
    }

    let payload: UpdatePaymentMethodPayload;

    if (paymentType === "offline") {
      payload = {
        appointmentId,
        paymentType: "offline",
        cashAmount: collectedAmount,
        onlineAmount: 0,
        splitPayments: [
          {
            paymentMethod: "cash",
            amount: collectedAmount,
          },
        ],
      };
    } else if (paymentType === "online") {
      payload = {
        appointmentId,
        paymentType: "online",
        cashAmount: 0,
        onlineAmount: collectedAmount,
        ...(paymentTxnId.trim() ? { paymentTxnId: paymentTxnId.trim() } : {}),
        splitPayments: [
          {
            paymentMethod: "online",
            amount: collectedAmount,
            ...(paymentTxnId.trim() ? { paymentTxnId: paymentTxnId.trim() } : {}),
            ...(razorpayOrderId.trim() ? { razorpayOrderId: razorpayOrderId.trim() } : {}),
          },
        ],
      };
    } else {
      const legs: SplitPaymentLeg[] = [];
      if (cash > 0) {
        legs.push({
          paymentMethod: "cash",
          amount: cash,
        });
      }
      if (online > 0) {
        legs.push({
          paymentMethod: "online",
          amount: online,
          ...(paymentTxnId.trim() ? { paymentTxnId: paymentTxnId.trim() } : {}),
          ...(razorpayOrderId.trim() ? { razorpayOrderId: razorpayOrderId.trim() } : {}),
        });
      }

      payload = {
        appointmentId,
        paymentType: "split",
        cashAmount: cash,
        onlineAmount: online,
        splitPayments: legs,
        ...(paymentTxnId.trim() ? { paymentTxnId: paymentTxnId.trim() } : {}),
      };
    }

    setIsUpdating(true);
    try {
      const response = await AppointmentService.updatePaymentMethod(payload);
      if (response.success) {
        toast.success(response.message || "Payment method updated successfully");
        onClose();
        reload();
      } else {
        toast.error(response.message || "Failed to update payment method");
      }
    } catch (error) {
      toast.error("Failed to update payment method");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs overflow-y-auto animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100 my-auto animate-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-linear-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-xl border border-emerald-100 text-emerald-600">
              <CreditCard className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Update Payment Method</h3>
              <p className="text-xs text-gray-500">
                Correct payment breakdown for {dhrId || "this appointment"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isUpdating}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-lg transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5 max-h-[78vh] overflow-y-auto">
          {/* Current Payment Summary Card */}
          <div className="bg-slate-50/80 p-4 rounded-xl border border-slate-100 space-y-2.5">
            <div className="flex justify-between items-center pb-2 border-b border-slate-200/60 text-xs">
              <span className="text-slate-500 font-medium">Total Consultation Fee:</span>
              <span className="font-bold text-slate-800 text-sm">₹{totalFee}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-emerald-700 font-semibold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
                Collected / Recorded Amount:
              </span>
              <span className="font-extrabold text-emerald-700 text-base">₹{collectedAmount}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1 text-[11px] text-slate-500">
              <div className="bg-white px-2.5 py-1.5 rounded-lg border border-slate-200/70 flex justify-between">
                <span>Current Cash:</span>
                <span className="font-semibold text-slate-700">₹{currentCash}</span>
              </div>
              <div className="bg-white px-2.5 py-1.5 rounded-lg border border-slate-200/70 flex justify-between">
                <span>Current Online:</span>
                <span className="font-semibold text-slate-700">₹{currentOnline}</span>
              </div>
            </div>
            {balanceDue > 0 && (
              <div className="flex justify-between items-center pt-1 text-[11px] text-amber-700 font-medium">
                <span>Remaining Balance Due:</span>
                <span>₹{balanceDue}</span>
              </div>
            )}
          </div>

          {/* Payment Type Selection */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
              Select New Payment Method
            </label>
            <div className="grid grid-cols-3 gap-2.5">
              {/* Cash / Offline Option */}
              <button
                type="button"
                onClick={() => handleTypeChange("offline")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition-all cursor-pointer ${
                  paymentType === "offline"
                    ? "bg-emerald-50 border-emerald-500 text-emerald-700 shadow-2xs ring-2 ring-emerald-500/20"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300"
                }`}
              >
                <Banknote className={`w-5 h-5 mb-1.5 ${paymentType === "offline" ? "text-emerald-600" : "text-gray-400"}`} />
                <span>Cash / Offline</span>
                <span className="text-[10px] text-gray-400 font-normal mt-0.5">100% Cash</span>
              </button>

              {/* Online Option */}
              <button
                type="button"
                onClick={() => handleTypeChange("online")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition-all cursor-pointer ${
                  paymentType === "online"
                    ? "bg-emerald-50 border-emerald-500 text-emerald-700 shadow-2xs ring-2 ring-emerald-500/20"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300"
                }`}
              >
                <CreditCard className={`w-5 h-5 mb-1.5 ${paymentType === "online" ? "text-emerald-600" : "text-gray-400"}`} />
                <span>Online (UPI/Card)</span>
                <span className="text-[10px] text-gray-400 font-normal mt-0.5">100% Online</span>
              </button>

              {/* Split Option */}
              <button
                type="button"
                onClick={() => handleTypeChange("split")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition-all cursor-pointer ${
                  paymentType === "split"
                    ? "bg-emerald-50 border-emerald-500 text-emerald-700 shadow-2xs ring-2 ring-emerald-500/20"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300"
                }`}
              >
                <Split className={`w-5 h-5 mb-1.5 ${paymentType === "split" ? "text-emerald-600" : "text-gray-400"}`} />
                <span>Split Payment</span>
                <span className="text-[10px] text-gray-400 font-normal mt-0.5">Cash + Online</span>
              </button>
            </div>
          </div>

          {/* Form Breakdown Area */}
          {paymentType === "offline" && (
            <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100/80 space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-emerald-800">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                Entire collected amount (₹{collectedAmount}) will be recorded as Cash.
              </div>
              <p className="text-[11px] text-gray-500 pl-6">
                Use this option if the patient paid in cash at the clinic counter.
              </p>
            </div>
          )}

          {paymentType === "online" && (
            <div className="space-y-3.5 bg-gray-50/60 p-4 rounded-xl border border-gray-100">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-800">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                Entire collected amount (₹{collectedAmount}) will be recorded as Online.
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                  <Hash className="w-3.5 h-3.5 text-gray-400" />
                  Transaction ID / UTR / Reference (Optional)
                </label>
                <input
                  type="text"
                  value={paymentTxnId}
                  onChange={(e) => setPaymentTxnId(e.target.value)}
                  placeholder="e.g. TXN123456, UPI Ref, etc."
                  className="w-full px-3 py-2 text-xs bg-white border border-gray-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Razorpay Order ID (Optional)
                </label>
                <input
                  type="text"
                  value={razorpayOrderId}
                  onChange={(e) => setRazorpayOrderId(e.target.value)}
                  placeholder="e.g. order_xyz"
                  className="w-full px-3 py-2 text-xs bg-white border border-gray-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>
            </div>
          )}

          {paymentType === "split" && (
            <div className="space-y-4 bg-gray-50/60 p-4 rounded-xl border border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-700">Specify Split Amounts</span>
                <span
                  className={`text-[11px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                    isSplitValid
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-rose-100 text-rose-700"
                  }`}
                >
                  {isSplitValid ? (
                    <>
                      <CheckCircle2 className="w-3 h-3" /> Total Matched (₹{currentSplitTotal})
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-3 h-3" /> Total ₹{currentSplitTotal} / ₹{collectedAmount}
                    </>
                  )}
                </span>
              </div>

              {/* Amount Inputs */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Cash Amount (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={collectedAmount}
                    value={cashAmount === 0 ? "" : cashAmount}
                    onChange={(e) => handleCashChange(Number(e.target.value) || 0)}
                    placeholder="0"
                    className="w-full px-3 py-2 text-xs font-semibold bg-white border border-gray-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Online Amount (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={collectedAmount}
                    value={onlineAmount === 0 ? "" : onlineAmount}
                    onChange={(e) => handleOnlineChange(Number(e.target.value) || 0)}
                    placeholder="0"
                    className="w-full px-3 py-2 text-xs font-semibold bg-white border border-gray-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Quick Split Presets */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const half = Math.round(collectedAmount / 2);
                    setCashAmount(half);
                    setOnlineAmount(collectedAmount - half);
                  }}
                  className="px-2.5 py-1 text-[11px] bg-white hover:bg-gray-100 text-gray-600 border border-gray-200 rounded-lg transition-colors cursor-pointer"
                >
                  50 / 50 Split
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const cash = Math.round(collectedAmount * 0.7);
                    setCashAmount(cash);
                    setOnlineAmount(collectedAmount - cash);
                  }}
                  className="px-2.5 py-1 text-[11px] bg-white hover:bg-gray-100 text-gray-600 border border-gray-200 rounded-lg transition-colors cursor-pointer"
                >
                  70% Cash / 30% Online
                </button>
              </div>

              {/* Online Leg Details */}
              {onlineAmount > 0 && (
                <div className="space-y-3 pt-2 border-t border-gray-200">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                      <Hash className="w-3.5 h-3.5 text-gray-400" />
                      Online Payment Txn ID / Reference (Optional)
                    </label>
                    <input
                      type="text"
                      value={paymentTxnId}
                      onChange={(e) => setPaymentTxnId(e.target.value)}
                      placeholder="e.g. TXN123456, UPI Ref, etc."
                      className="w-full px-3 py-2 text-xs bg-white border border-gray-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Razorpay Order ID (Optional)
                    </label>
                    <input
                      type="text"
                      value={razorpayOrderId}
                      onChange={(e) => setRazorpayOrderId(e.target.value)}
                      placeholder="e.g. order_xyz"
                      className="w-full px-3 py-2 text-xs bg-white border border-gray-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end items-center gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={isUpdating}
            className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isUpdating || !isSplitValid || collectedAmount <= 0}
            className="flex items-center gap-2 px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-98 rounded-xl shadow-sm transition-all disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
          >
            {isUpdating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4" />
                Update Payment Method
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
